/**
 * A grip on every corner of a window, revealed while the interface is being
 * rearranged.
 *
 * Written once. A window that can be dragged should be able to be resized, and
 * the arithmetic for it is the same every time: the corner being pulled follows
 * the pointer and the corner opposite stands still, which for a window that can
 * be anywhere means moving it as well as changing its size.
 *
 * A window has no size of its own until somebody gives it one. Until then it is
 * whatever its contents need, which is right: a bank is as tall as a bank. The
 * first drag of a grip measures what is there and takes it from the browser,
 * and from then on the size is somebody's and is remembered.
 */

import { useCallback, useEffect, useState } from 'react';
import { keepSize, LAYOUT_EVENT, sizeFor } from './layout';
import { CORNERS, CORNER_CURSORS, resizeFrom, roomFor, type Corner } from './resize';

/** How small any window may be dragged. */
const LEAST = { width: 180, height: 120 };

export interface Grippable {
  /** The width and height to apply, or nothing while it is content sized. */
  size: { width: number; height: number } | null;
  /** The grips themselves, to put inside the window. */
  grips: JSX.Element | null;
}

/** The size kept for a window on this screen, no smaller than it may go. */
function stored(
  kind: string,
  least: { width: number; height: number },
): { width: number; height: number } | null {
  const held = sizeFor(kind);
  if (!held) return null;
  return {
    width: Math.max(least.width, held.width),
    height: Math.max(least.height, held.height),
  };
}

export function useWindowGrips(
  kind: string,
  /** Moving the window, which a pull on a top or left corner has to do. */
  nudge: (dx: number, dy: number) => void,
  /** Whether the grips are showing, which is while Alt is held. */
  showing: boolean,
  least: { width: number; height: number } = LEAST,
): Grippable {
  /*
   * The size kept under this name, read again whenever the name changes. A gauge
   * keeps one size per shape under a name per shape, so that turning a bar on
   * its end and back again brings back the bar that was there, rather than the
   * one size being bent from one shape to the other.
   */
  const [held, setHeld] = useState(() => ({ kind, size: stored(kind, least) }));
  if (held.kind !== kind) setHeld({ kind, size: stored(kind, least) });
  const size = held.kind === kind ? held.size : stored(kind, least);

  // Asked again for a different size of screen or a different layout, since
  // either can have a size of its own for this window.
  const leastWidth = least.width;
  const leastHeight = least.height;
  useEffect(() => {
    const again = (): void =>
      setHeld({ kind, size: stored(kind, { width: leastWidth, height: leastHeight }) });
    window.addEventListener('resize', again);
    window.addEventListener(LAYOUT_EVENT, again);
    return () => {
      window.removeEventListener('resize', again);
      window.removeEventListener(LAYOUT_EVENT, again);
    };
  }, [kind, leastWidth, leastHeight]);
  const setSize = useCallback(
    (next: { width: number; height: number }) => setHeld({ kind, size: next }),
    [kind],
  );

  const resize = useCallback(
    (corner: Corner) =>
      (event: React.PointerEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        const held = (event.currentTarget as HTMLElement).parentElement;
        if (!held) return;

        const box = held.getBoundingClientRect();
        let last = { x: event.clientX, y: event.clientY };
        let now = {
          width: Math.max(least.width, Math.round(box.width)),
          height: Math.max(least.height, Math.round(box.height)),
        };

        const move = (moved: PointerEvent): void => {
          const dx = moved.clientX - last.x;
          const dy = moved.clientY - last.y;
          last = { x: moved.clientX, y: moved.clientY };

          const rect = held.getBoundingClientRect();
          const room = roomFor(
            { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
            corner,
            { width: window.innerWidth, height: window.innerHeight },
          );

          const next = resizeFrom({ x: 0, y: 0, ...now }, corner, dx, dy, {
            minWidth: least.width,
            minHeight: least.height,
            maxWidth: now.width + room,
            maxHeight: now.height + room,
          });

          if (next.width === now.width && next.height === now.height) return;
          now = { width: next.width, height: next.height };

          setSize(now);
          if (next.x !== 0 || next.y !== 0) nudge(next.x, next.y);
        };

        const done = (): void => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', done);
          keepSize(kind, now);
        };

        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', done);
      },
    [kind, least.height, least.width, nudge, setSize],
  );

  return {
    size,
    grips: showing ? (
      <>
        {CORNERS.map((corner) => (
          <div
            key={corner}
            className={`window-grip ${corner}`}
            style={{ cursor: CORNER_CURSORS[corner] }}
            title="Drag to resize"
            onPointerDown={resize(corner)}
            aria-hidden
          />
        ))}
      </>
    ) : null,
  };
}
